import React from 'react';
import type { GroupMemberWithProfile, GroupMemberRole } from '@ext-bookmark/shared/types';

interface MemberCardProps {
  member: GroupMemberWithProfile;
  currentUserRole: GroupMemberRole;
  currentUserId: string;
  onRoleChange?: (userId: string, newRole: GroupMemberRole) => void;
  onRemove?: (userId: string) => void;
}

const MemberCard: React.FC<MemberCardProps> = ({
  member,
  currentUserRole,
  currentUserId,
  onRoleChange,
  onRemove,
}) => {
  const isCurrentUser = member.userId === currentUserId;
  const isOwner = member.role === 'owner';
  const isAdmin = member.role === 'admin';
  const _isMember = member.role === 'member';
  void _isMember;

  // 权限检查
  const canManageRole = (currentUserRole === 'owner' || currentUserRole === 'admin') && !isOwner && !isCurrentUser;
  const canRemove = (currentUserRole === 'owner' || currentUserRole === 'admin') && !isOwner && !isCurrentUser;

  const getRoleBadge = () => {
    if (isOwner) {
      return (
        <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded">
          创建者
        </span>
      );
    }
    if (isAdmin) {
      return (
        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded">
          管理员
        </span>
      );
    }
    return (
      <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
        成员
      </span>
    );
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value as GroupMemberRole;
    if (onRoleChange) {
      onRoleChange(member.userId, newRole);
    }
  };

  const handleRemove = () => {
    if (onRemove && confirm(`确定要移除成员"${member.username || member.email}"吗？`)) {
      onRemove(member.userId);
    }
  };

  // 显示名称：优先显示用户名，如果用户名为空则显示邮箱
  const displayName = member.username?.trim() || member.email || '未知用户';
  // 如果用户名和邮箱都存在，显示邮箱作为副标题
  const showEmailAsSubtitle = member.username?.trim() && member.email && member.username !== member.email;

  return (
    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {displayName}
          </div>
          {getRoleBadge()}
          {isCurrentUser && (
            <span className="text-xs text-gray-500 dark:text-gray-400">(我)</span>
          )}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {showEmailAsSubtitle ? (
            <span>{member.email}</span>
          ) : (
            <span>加入于 {new Date(member.joinedAt).toLocaleDateString('zh-CN')}</span>
          )}
        </div>
        {showEmailAsSubtitle && (
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            加入于 {new Date(member.joinedAt).toLocaleDateString('zh-CN')}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {canManageRole && (
          <div className="relative group">
            <select
              value={member.role}
              onChange={handleRoleChange}
              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="member">成员</option>
              <option value="admin">管理员</option>
              {currentUserRole === 'owner' && <option value="owner">创建者</option>}
            </select>
          </div>
        )}
        {canRemove && (
          <button
            onClick={handleRemove}
            className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            移除
          </button>
        )}
      </div>
    </div>
  );
};

export default MemberCard;
